// RECALL-PROCEDURES: the playbook is the procedure. On the playbook strip, a "Procedure" chip with
// its runs ("12 runs · 2 failed") and whether Recall holds the procedure; clicking it opens the
// run history in a modal (result, the DE that ran it, the task, failed and skipped steps with
// their notes, when). One list, here: nothing else lists procedure runs.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { ListOrderedIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { RecallStateBadge } from "../memory/rules";
import { ago } from "./doc-kinds";
import { workKeys } from "./work-queries";

export type PlaybookProcedure = OperationOutput<"pages.playbookProcedure">;
type Run = PlaybookProcedure["recent"][number];

export const playbookProcedureQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: [...workKeys.all(co), "playbook-procedure", id] as const,
    queryFn: () => api.client.pages.playbookProcedure({ params: { co, id } }),
    staleTime: 30_000,
  });

export function runsLine(r: PlaybookProcedure["runs"]): string {
  if (r.total === 0) return "No runs yet";
  const runs = `${r.total} ${r.total === 1 ? "run" : "runs"}`;
  return r.failed > 0 ? `${runs} · ${r.failed} failed` : runs;
}

const RESULT: Record<Run["result"], [string, "success" | "danger" | "neutral"]> = {
  succeeded: ["Succeeded", "success"],
  failed: ["Failed", "danger"],
  abandoned: ["Abandoned", "neutral"],
};

/** The chip on the strip; nothing until the page is a procedure or has a run. */
export function ProcedureChip({ api, co, pageId }: { api: Api; co: string; pageId: string }) {
  const q = useQuery(playbookProcedureQuery(api, co, pageId));
  const [open, setOpen] = useState(false);
  const p = q.data;
  if (!p || (!p.procedure && p.runs.total === 0)) return null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1" data-slot="playbook-procedure">
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-6 items-center gap-1 rounded-full border border-subtle bg-card px-2 text-xs text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
      >
        <ListOrderedIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
        <span className="text-faint">Procedure</span>
        <span className="font-medium tabular-nums" data-slot="procedure-runs">
          {runsLine(p.runs)}
        </span>
      </button>
      {p.procedure ? (
        <RecallStateBadge
          state={p.procedure.recall_state}
          detail={p.procedure.recall_detail}
          note="Whether Recall holds this playbook as a procedure."
        />
      ) : null}
      {open ? <RunHistory procedure={p} onClose={() => setOpen(false)} /> : null}
    </span>
  );
}

function RunHistory({
  procedure: p,
  onClose,
}: {
  procedure: PlaybookProcedure;
  onClose: () => void;
}) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-slot="procedure-runs-dialog">
        <DialogHeader>
          <DialogTitle>Procedure runs</DialogTitle>
          <DialogDescription>
            {p.procedure?.title ? `${p.procedure.title} · ` : ""}
            {runsLine(p.runs)}
          </DialogDescription>
        </DialogHeader>
        {p.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-slot="procedure-runs-empty">
            No runs recorded yet
          </p>
        ) : (
          <ul className="-mx-1 flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-1 [scrollbar-width:thin]">
            {p.recent.map((r) => (
              <RunItem key={r.id} run={r} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RunItem({ run: r }: { run: Run }) {
  const [label, tone] = RESULT[r.result];
  const marked = r.steps.filter((s) => s.result === "failed" || s.result === "skipped");
  return (
    <li
      className="flex min-w-0 flex-col gap-1 text-sm"
      data-slot="procedure-run"
      data-result={r.result}
    >
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        <Badge variant={tone} className="h-5 text-[11px]">
          {label}
        </Badge>
        <span className="inline-flex items-center gap-1 text-foreground">
          {workerName(r.by.name)}
          {r.by.kind === "digital_employee" ? <WorkerBadge kind="de" size="sm" /> : null}
        </span>
        <span className="ml-auto text-xs text-faint">{ago(r.created_at)}</span>
      </span>
      <span className="min-w-0 truncate text-[13px] text-muted-foreground" title={r.task}>
        {r.task}
      </span>
      {marked.length ? (
        <ul className="flex flex-col gap-0.5 pl-3 text-xs text-muted-foreground">
          {marked.map((s) => (
            <li key={s.n}>
              Step {s.n} {s.result}
              {s.note ? `: ${s.note}` : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {r.note ? <span className="text-xs text-muted-foreground">{r.note}</span> : null}
    </li>
  );
}
